/*
  Date rules shared by the event listing and details pages. The backend
  applies the same end-of-day IST cut-off when it accepts bookings and
  cancellations, so the two sides agree on when an event is over.

  Calculated in IST directly so the member's device time zone does not move
  the cut-off.
*/

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

// An event stays current for the whole of its day in IST and only counts as
// past from the following midnight. Undated events never become past.
export const isEventPast = (event, now = Date.now()) => {
  const time = toTime(event?.eventDate);
  if (time === null) return false;
  const shifted = time + IST_OFFSET_MS;
  const endOfDay = shifted - (shifted % DAY_MS) + DAY_MS - IST_OFFSET_MS;
  return now >= endOfDay;
};

/*
  Booking state for registration events, null for every other type:
    past      - the event day has ended
    closed    - closed or drawn by the admin, or the close date has passed
    not-open  - the open date is still ahead
    open      - bookable now
*/
export const getBookingState = (event, now = Date.now()) => {
  if (event?.type !== 'registration') return null;
  if (isEventPast(event, now)) return 'past';
  if (event.status !== 'upcoming' && event.status !== 'active') return 'closed';

  const openTime = toTime(event.registrationOpenDate);
  if (openTime !== null && now < openTime) return 'not-open';

  const closeTime = toTime(event.registrationCloseDate);
  if (closeTime !== null && now > closeTime) return 'closed';

  return 'open';
};

// Which listing tab an event belongs to. Past is decided by date alone; the
// rest follows the admin-set status, and closed / drawn events that have not
// happened yet stay in Active.
export const getDisplayState = (event, now = Date.now()) => {
  if (isEventPast(event, now)) return 'past';
  if (event?.status === 'upcoming') return 'upcoming';
  return 'active';
};
